import React, { useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { notify } from '../../../utils/notification';
import { decideDefenderReaction } from '../utils/combatSaveStateService';
import { reactionSlotOptions } from '../utils/reactionSlotOptions';
import { SHIELD_AC_BONUS, SHIELD_FEATURE_ID, shieldStopsAttack } from '../../../shared/rules/effects/reactionSpells.mjs';

/**
 * Shield when an attack hits: the Reaction and a slot give +5 AC until the start of your next turn, counting against that
 * attack. It comes first; skipping leaves any other Reaction the hit offered for its own prompt.
 */
export default function ShieldSpellPrompt({ campaignId, characterId, form, combatState, onCombatChange }) {
  const [busy, setBusy] = useState(false);
  const [slot, setSlot] = useState('0');
  const lock = useRef(false);
  const offer = (combatState?.reactionOpportunities || []).find((o) => o.status === 'pending' && String(o.targetCombatantId) === String(characterId)
    && o.candidateFeatures?.includes(SHIELD_FEATURE_ID));
  const slots = offer ? reactionSlotOptions(form) : [];
  const chosen = slots[Number(slot)] || slots[0];
  const decide = async (decision) => {
    if (lock.current || !offer) return;
    lock.current = true; setBusy(true);
    try {
      const result = await decideDefenderReaction(campaignId, offer.opportunityId, decision, SHIELD_FEATURE_ID, decision === 'use' ? { pool: chosen?.pool, slotLevel: chosen?.slotLevel } : {});
      onCombatChange?.(result.combat);
      setSlot('0');
      if (decision === 'use') notify(result.opportunity?.attackNegated ? 'Shield: the attack misses.' : `Shield: +${SHIELD_AC_BONUS} AC until your next turn.`, 'success');
    } catch (error) { notify(error.message || 'Shield could not be cast.', 'danger'); }
    finally { lock.current = false; setBusy(false); }
  };
  const raised = Number(offer?.armorClass) + SHIELD_AC_BONUS;
  return (
    <Modal show={Boolean(offer)} centered backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label="Shield">
      <Modal.Header><Modal.Title>Shield</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{offer?.attackName || 'An attack'} hit you: {offer?.attackTotal} against AC {offer?.armorClass}{offer?.rawDamage ? `, for ${offer.rawDamage} damage` : ''}.</p>
        <p>{offer && (shieldStopsAttack(offer) ? `Shield raises your AC to ${raised}, so this attack misses.`
          : offer.critical ? `A Critical Hit lands anyway, but your AC would be ${raised} until your next turn.`
            : `The attack still hits at AC ${raised}, but your AC stays higher until your next turn.`)}</p>
        {slots.length > 1 && <Form.Select aria-label="Spell slot" value={slot} disabled={busy} onChange={(event) => setSlot(event.target.value)}>
          {slots.map((option, index) => <option key={`${option.pool}:${option.slotLevel}`} value={index}>{option.label}</option>)}
        </Form.Select>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => decide('skip')}>Skip</Button>
        <Button disabled={busy || !chosen} onClick={() => decide('use')}>{busy ? 'Saving…' : 'Cast Shield'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
